import type { CSSProperties } from 'react';
import { Text } from '../../../atoms/Text/Text';
import { TableHeader } from '../../../molecules/TableHeader/TableHeader';
import { Table, type TableColumn, type TableRowData } from '../../../organisms/Table/Table';
import { spanClass, type WidgetBlockProps, type WidgetStatus } from '../shared/cardShell';
import styles from './TopicRankingTable.module.css';

/** A topic's mentions split by sentiment, as shares of its own mentions (0–100, summing to 100). */
export type SentimentShares = { positive: number; neutral: number; negative: number };

/** One ranked topic, as drawn. */
export type TopicRankingEntry = {
  /** The topic's key — the row key, and what `onOpen` is called with. */
  id: string;
  /** The topic's name. */
  topic: string;
  /** The count the table ranks by, formatted (`1.2K`). */
  count: string;
  /** That count's part of the whole it is a part of, formatted (`18%`). */
  contribution: string;
  /** The topic's own mentions by sentiment — the bar. */
  shares: SentimentShares;
  /** The share the table is about, named (`71% negative`) — written under the bar. */
  shareLabel: string;
};

export type TopicRankingView = { entries: TopicRankingEntry[] };

export type TopicRankingTableProps = WidgetBlockProps & {
  view?: TopicRankingView;
  title: string;
  helpText: string;
  helpSupportingText?: string;
  /** What the table says when the period has no such topic. */
  emptyLabel: string;
  /** Makes each row open that topic's conversations. */
  onOpen?: (id: string) => void;
};

const SENTIMENTS = ['positive', 'neutral', 'negative'] as const;

/**
 * The sentiment mix of one topic: three segments, as wide as their shares,
 * coloured by meaning (`progress-*-surface`, as `Badge` and every sentiment
 * mark), with the share the table is about written under it so the colour is
 * never the only reading. The bar's name lists all three for a screen reader.
 */
function ShareBar({ shares, label }: { shares: SentimentShares; label: string }) {
  const name = SENTIMENTS.map((s) => `${Math.round(shares[s])}% ${s}`).join(', ');
  return (
    <span className={styles.share}>
      <span className={styles.bar} role="img" aria-label={name}>
        {SENTIMENTS.filter((s) => shares[s] > 0).map((s) => (
          <span
            key={s}
            className={`${styles.segment} ${styles[s]}`}
            /* The one data-driven length: a share of the bar's width. */
            style={{ '--share': shares[s] } as CSSProperties}
          />
        ))}
      </span>
      <Text as="span" size="xs" leading="none" color="secondary">
        {label}
      </Text>
    </span>
  );
}

function emptyLabelFor(status: WidgetStatus, empty: string): string {
  if (status === 'loading') return 'Loading…';
  if (status === 'error') return 'Couldn’t load this widget.';
  if (status === 'notConnected') return 'Live data for this widget is not connected yet.';
  return empty;
}

/**
 * The executive Customer voice's topic tables (GP, 2026-11-05): the top five
 * topics by a count — every mention, negative ones, positive ones — each with
 * the count, its part of the whole and the topic's own sentiment mix. No move
 * against the comparison period: GP asked for these without `% change`.
 */
export function TopicRankingTable({
  status,
  view,
  span = 4,
  title,
  helpText,
  helpSupportingText,
  emptyLabel,
  onOpen,
}: TopicRankingTableProps) {
  const entries = status === 'ready' && view ? view.entries : [];
  /*
   * One word a header, and no help icon on a column: at four across a third of
   * the row (336 wide at 1280) every pixel goes to the topic's name. What is
   * counted, and of what whole, is the table's help.
   */
  const columns: TableColumn[] = [
    { key: 'topic', header: 'Topic', width: '34%' },
    { key: 'count', header: 'Count', width: '16%' },
    { key: 'contribution', header: 'Contribution', width: '22%' },
    { key: 'sentiment', header: 'Sentiment', width: '28%' },
  ];
  const rows: TableRowData[] = entries.map((entry) => ({
    key: entry.id,
    cells: {
      topic: { text: entry.topic },
      count: { text: entry.count },
      contribution: { text: entry.contribution },
      sentiment: { content: <ShareBar shares={entry.shares} label={entry.shareLabel} /> },
    },
    ...(onOpen && {
      onActivate: () => onOpen(entry.id),
      activateLabel: `Open the conversations about ${entry.topic}`,
    }),
  }));
  return (
    <Table
      className={spanClass(span)}
      label={title}
      header={
        <TableHeader title={title} helpText={helpText} helpSupportingText={helpSupportingText} />
      }
      columns={columns}
      rows={rows}
      emptyLabel={emptyLabelFor(status, emptyLabel)}
    />
  );
}
